'use client';

import React, { useState } from 'react';

export interface NewsletterFormTranslations {
  title?: string;
  placeholder: string;
  buttonSubmit: string;
  buttonSubmitting: string;
  privacyText: string;
  privacyLink: string;
  successMessage: string;
  errorMessage: string;
}

export interface NewsletterFormProps {
  translations: NewsletterFormTranslations;
  privacyPolicyUrl?: string;
  apiEndpoint?: string;
  className?: string;
  locale?: string;
}

export function NewsletterForm({
  translations,
  privacyPolicyUrl = '/legal/politika-konfidentsialnosti',
  apiEndpoint = '/api/newsletter',
  className = '',
  locale,
}: NewsletterFormProps) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setMessage('');
    setIsSubmitting(true);
    try {
      const res = await fetch(apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, comment: '', locale }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setMessage(data.message || translations.successMessage);
        setEmail('');
      } else {
        setMessage(data.message || translations.errorMessage);
      }
    } catch (err) {
      setMessage(translations.errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Определяем, является ли сообщение успешным (используем ключевые слова из разных языков)
  const isSuccessMessage = 
    message.includes('успеш') || 
    message.toLowerCase().includes('success') || 
    message.toLowerCase().includes('erfolgreich') ||
    message.toLowerCase().includes('successful');

  return (
    <form onSubmit={handleSubmit} className={`w-full max-w-md ${className}`}>
      <div className="flex gap-x-4">
        <label htmlFor="email-address" className="sr-only">
          Email
        </label>
        <input
          id="email-address"
          name="email"
          type="email"
          required
          placeholder={translations.placeholder}
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="min-w-0 flex-auto rounded-md bg-white/5 px-3.5 py-2 text-base text-white outline-1 -outline-offset-1 outline-white/10 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-500 sm:text-sm/6 dark:outline-white/20"
        />
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex-none rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-900 shadow-xs hover:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-70 dark:bg-white/90 dark:shadow-none dark:hover:bg-white"
        >
          {isSubmitting ? translations.buttonSubmitting : translations.buttonSubmit}
        </button>
      </div>
      <p className="mt-4 text-sm/6 text-gray-300">
        {translations.privacyText}{' '}
        <a
          href={privacyPolicyUrl}
          className="font-semibold whitespace-nowrap text-white hover:text-gray-100 dark:hover:text-gray-200"
        >
          {translations.privacyLink}
        </a>
        .
      </p>
      {message && (
        <div
          className={`mt-4 p-3 rounded-md border ${
            isSuccessMessage
              ? 'border-green-500 text-green-200 bg-green-900/30'
              : 'border-red-500 text-red-200 bg-red-900/30'
          }`}
        >
          {message}
        </div>
      )}
    </form>
  );
}
